import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Menu, X } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { trackEvent } from "../lib/analytics";
import { SplitButton } from "./ui";

const navItems = [
  { to: "/solutions", label: "خدمات و راهکارها" },
  { to: "/case-studies", label: "مطالعات موردی" },
  { to: "/showcase", label: "نمونه‌های تعاملی" },
  { to: "/approach", label: "رویکرد ما" },
  { to: "/about", label: "درباره چاره‌یار" },
];

export function Header({ inverse = false, overlay = false }: { inverse?: boolean; overlay?: boolean }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <motion.header
      className={`site-header${inverse ? " site-header--inverse" : ""}${overlay ? " site-header--overlay" : ""}`}
      initial={reduce ? false : { y: -14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
    >
      <a className="skip-link" href="#main-content">رفتن به محتوای اصلی</a>
      <div className="site-header__inner">
        <Link to="/" className="brand" aria-label="چاره‌یار — صفحه اصلی">
          <img
            className="brand__logo"
            src={inverse ? "/identity/charehyar-logo-light-v2.png" : "/identity/charehyar-logo-primary-v2.png"}
            alt="چاره‌یار"
            width="2092"
            height="524"
            decoding="async"
          />
        </Link>
        <nav className="desktop-nav" aria-label="ناوبری اصلی">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/"}>{item.label}</NavLink>
          ))}
        </nav>
        <SplitButton to="/contact" variant={inverse ? "copper" : "dark"} className="header-cta" tracking={{ label: "مسئله‌تان را مطرح کنید", location: "desktop_header" }}>مسئله‌تان را مطرح کنید</SplitButton>
        <button className="menu-button" type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} aria-controls="mobile-navigation" aria-label={open ? "بستن منو" : "بازکردن منو"}>
          {open ? <X /> : <Menu />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div id="mobile-navigation" className="mobile-menu" initial={reduce ? false : { opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.2 }}>
            <nav aria-label="ناوبری موبایل">
              {navItems.map((item, index) => (
                <motion.div key={item.to} initial={reduce ? false : { opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: index * 0.035 }}>
                  <NavLink to={item.to} end={item.to === "/"}>{item.label}</NavLink>
                </motion.div>
              ))}
            </nav>
            <SplitButton to="/contact" variant="dark" tracking={{ label: "مسئله‌تان را مطرح کنید", location: "mobile_menu" }}>مسئله‌تان را مطرح کنید</SplitButton>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-footer__top">
        <div className="site-footer__identity">
          <Link to="/" className="site-footer__brand" aria-label="چاره‌یار — صفحه اصلی">
            <img src="/identity/charehyar-logo-light-v2.png" alt="چاره‌یار" width="2092" height="524" loading="lazy" decoding="async" />
          </Link>
          <p>از تشخیص مسئله تا اجرای راهکار</p>
          <a className="site-footer__email" href="mailto:info@charehyar.ir" dir="ltr" onClick={() => trackEvent("email_click", { location: "footer", destination: "email" })}>info@charehyar.ir</a>
        </div>
        <nav aria-label="پیوندهای فوتر">
          <Link to="/about">درباره چاره‌یار</Link>
          <Link to="/solutions">خدمات و راهکارها</Link>
          <Link to="/case-studies">مطالعات موردی</Link>
          <Link to="/showcase">نمونه‌های تعاملی</Link>
          <Link to="/approach">رویکرد ما</Link>
          <Link to="/contact">تماس با ما</Link>
          <Link to="/privacy">حریم خصوصی و محرمانگی</Link>
        </nav>
      </div>
      <div className="site-footer__bottom"><p>© ۱۴۰۵ چاره‌یار — تمامی حقوق محفوظ است.</p></div>
    </footer>
  );
}

export function PageShell({ children, inverseHeader = false }: { children: ReactNode; inverseHeader?: boolean }) {
  return <><Header inverse={inverseHeader} />{children}<Footer /></>;
}
